import type { UseCaseConfig } from "../types";
import { CORE_FIELDS } from "../shared/form-fields";
import { USE_CASE_SEO } from "../seo-manifest";
import { hrefForUseCase } from "../links";
import { TS_CASE_STUDY_HREF } from "../case-studies/teachingStrategies";
import { CrossLink } from "../../components/landing/CrossLink";

/**
 * Research-to-Leads use case ("From Research, to Leads"). Targets the digital
 * research marketer who publishes their organization's own original research
 * (benchmark reports, "State of X" studies, white papers) to drive leads.
 * Forward narratives don't replace or compete with the report. They lift the
 * distribution work already happening (gated downloads, webinar sign-ups,
 * follow-ups) and, most of all, keep the research working year-round instead of
 * going quiet a month after launch. The narrative `reportId` points at a demo
 * built from a fictional industry research report; see
 * `use-cases/industry-reporting/brief.md` for the full campaign brief.
 */
export const industryReportingConfig: UseCaseConfig = {
  slug: "industry-reporting",
  menuLabel: "Research Reporting",
  navLabel: "Use Case · Research Reporting",
  teaser:
    "Turn your published research into narratives that keep generating leads all year.",
  meta: USE_CASE_SEO["industry-reporting"],
  hero: {
    eyebrowLabel: "Use Case",
    subhead: "Research Reporting",
    headlineLead: "Same research report. ",
    headlineEmphasis: "More leads.",
    body: "You publish original research to drive leads, then most of it goes quiet a month after launch. Forward turns your report’s findings into personalized, interactive narratives that lift the work you’re already doing across gated downloads, webinar sign-ups, and follow-ups, and keep the research working all year, resurfaced for the moments that matter. It doesn’t replace your report. It gets more people to read it.",
    byline: "One report. Working all year, not just launch week.",
  },
  narrativeSample: {
    reportId: "senthel_state_of_cloud_security_2026_c4x9k2",
    badgeLabel: "Sample",
    sampleTitle: "Sample: a research report as a narrative",
    eyebrow: "The format",
    captionHeadline: "Your findings, in a form people actually want.",
    captionBody:
      "Tap or swipe through a published research report rebuilt as a Forward narrative. Each section pairs one headline finding with the context behind it, sized for a phone and shaped to the brand, so your insights actually get read, and you can see which ones landed.",
    disclosure: (
      <>
        The sample is <em>The 2026 State of Cloud Security</em>, a fictional
        benchmark report from <em>Senthel</em>, built to demonstrate the format.
        We build these from your own published research. No underlying data
        needed. For a live campaign, see the{" "}
        <CrossLink href={TS_CASE_STUDY_HREF}>
          Teaching Strategies case study
        </CrossLink>
        .
      </>
    ),
    fullScreenLabel: "Open the sample full screen →",
    ctaLabel: "Open sample",
    watermark: "State of",
  },
  moments: {
    eyebrow: "Fits the distribution you already run",
    headline: "It lifts every touchpoint, and keeps working long after launch.",
    items: [
      {
        title: "Gated download",
        body: "A narrative preview alongside the form lifts opt-ins, and gives readers a reason to grab the full report.",
      },
      {
        title: "Email campaign",
        body: "Your launch email links to a narrative that gets opened and read, then routes to the report itself.",
      },
      {
        title: "Webinar sign-ups",
        body: "A short teaser narrative of the key findings drives registrations and primes attendees to show up.",
      },
      {
        title: "Webinar follow-up",
        body: "The recap as the centerpiece of your follow-up: the findings that landed, not a slide-deck link.",
      },
      {
        eyebrow: "The big opportunity",
        title: "Year-round resurfacing",
        body: "A month after launch, the report goes quiet, but the insights are still relevant. Re-cut it for a new moment or segment and put it back to work, all year.",
      },
    ],
  },
  scale: {
    eyebrow: "From one launch to a year of relevant moments",
    leadIn: "Whether it’s ",
    firstHighlight: "one launch campaign",
    middle: (
      <>
        {" "}
        or <br className="hidden md:block" />
      </>
    ),
    secondHighlight: "a year of resurfaced moments,",
    tail: (
      <>
        <br />
        the same report keeps earning its keep.
      </>
    ),
    subtitle: (
      <>
        Launch the report as a narrative, then keep it working: re-cut it for a
        new segment, tie a finding to a moment in the news, or update one stat
        and send it as a &ldquo;mid-year read.&rdquo; Brand, voice, and pacing
        stay consistent every time.
      </>
    ),
    counters: ["Once", "Quarterly", "Monthly", "Weekly", "Year-Round"],
    footnote: (
      <>
        Have customer usage data, too?{" "}
        <CrossLink href={hrefForUseCase("customer-marketing")} bold>
          Give every account its own milestone recap
        </CrossLink>
        .
      </>
    ),
  },
  pillars: {
    eyebrow: "Built for teams that publish research",
    headline: "Three reasons your team can actually pull this off.",
    points: [
      {
        title: "We work from your published report.",
        body: "No raw data, no survey responses, no internal databases, no IT ticket. If the report is on your website, we can build from it.",
      },
      {
        title: "Typically 4–8 weeks to live.",
        body: "From handoff to live narratives, typically 4–8 weeks. Distribute to your existing audience via a CSV export from the email platform you already use.",
      },
      {
        title: "You see which findings landed.",
        body: "Section-level engagement, time-in-narrative, bookmarks, and CTA clicks tell you which insights resonated and which readers want a conversation, so you know exactly what to resurface next.",
      },
    ],
  },
  cta: {
    sectionEyebrow: "Your research",
    headlineLead: "What would ",
    headlineEmphasis: "your research",
    headlineTail:
      " feel like, personalized and packaged in a form people actually want?",
    body: "Point us at a report you’ve published. We’ll build a sample Forward narrative from it, working only from what you’ve already made public. No data handoff, no demo, no pitch. Just the thing itself, so you can see what your research could become.",
    submitLabel: "Send it →",
    sendingLabel: "Sending…",
    continueLabel: "Continue →",
    success: {
      headline: "We’ll be in touch.",
      body: "Someone from our team will reach out within one to two business days, often with a sample narrative built from the research you pointed us to.",
    },
    fields: [
      ...CORE_FIELDS,
      {
        key: "research_url",
        type: "text",
        label: "Link to a report you’ve published",
        placeholder: "https://yourcompany.com/research/state-of-2026",
        required: false,
        core: false,
        helpText:
          "A public PDF or landing page is enough. We build from the report itself.",
      },
      {
        key: "research_cadence",
        type: "select",
        label: "How often do you publish original research?",
        required: true,
        core: false,
        options: [
          { value: "multiple-per-year", label: "Multiple reports a year" },
          { value: "annual", label: "About once a year" },
          { value: "occasional", label: "Occasionally / one-off" },
          { value: "planning-first", label: "Planning our first" },
        ],
      },
      {
        key: "distribution_pain",
        type: "select",
        label: "Where does your research lose the most momentum?",
        required: true,
        core: false,
        allowOther: true,
        options: [
          { value: "low-engagement", label: "Downloads don’t turn into reads" },
          {
            value: "webinar-followup",
            label: "Webinar sign-ups & follow-up fall flat",
          },
          { value: "forgotten", label: "It’s forgotten a month after launch" },
          { value: "no-repurpose", label: "We never repurpose it year-round" },
        ],
      },
    ],
  },
};
